import { useState, useEffect, useRef } from 'react';
import { adminService, inventoryService } from '../../services/supabase';
import AnalyticsDashboard from '../dashboard/AnalyticsDashboard';
import InvoicesApp from '../pos/InvoicesApp';
import ReportsApp from '../pos/ReportsApp';
import CRMApp from '../pos/CRMApp';
import BillingSettings from '../pos/BillingSettings';

// ── Theme (matches AdminDashboard) ─────────────────────────────
const T = {
  primary:      '#FF4444',
  primaryDark:  '#e03333',
  primaryLight: '#fff5f5',
  gradient:     'linear-gradient(135deg, #FF4444, #FF6B00)',
  bg:           '#f5f7fa',
  surface:      '#ffffff',
  border:       '#e5e7eb',
  text:         '#1f2937',
  muted:        '#6b7280',
  green:        '#16a34a',
  greenBg:      '#f0fdf4',
  greenBorder:  '#bbf7d0',
  red:          '#dc2626',
  redBg:        '#fef2f2',
  redBorder:    '#fecaca',
  orange:       '#ea580c',
  sidebarW:     '240px',
};

const NAV_ITEMS = [
  { key: 'Analytics',        label: 'Analytics' },
  { key: 'Menu',             label: 'Menu' },
  { key: 'Invoices',         label: 'Invoices' },
  { key: 'Reports',          label: 'Reports' },
  { key: 'Tables',           label: 'Tables' },
  { key: 'Orders',           label: 'Orders' },
  { key: 'Waiters',          label: 'Waiters' },
  { key: 'Customers',        label: 'Customers' },
  { key: 'Inventory',        label: 'Inventory' },
  { key: 'BillingSettings',  label: 'Billing Settings' },
  { key: 'Profile',          label: 'Profile' },
];

const AdminRestaurantManagement = ({ restaurant: restaurantProp, onBack, onPosToggled }) => {
  const [restaurant,       setRestaurant]       = useState(restaurantProp);
  const [activeTab,        setActiveTab]        = useState(() => sessionStorage.getItem('admin_active_tab') || 'Analytics');
  const [posEnabled,       setPosEnabled]       = useState(restaurantProp.menuron_lite ?? false);
  const [qrAppEnabled,     setQrAppEnabled]     = useState(restaurantProp.qr_app_enabled ?? false);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [posSaving,        setPosSaving]        = useState(false);
  const [qrSaving,         setQrSaving]         = useState(false);
  const [confirmDisablePOS,setConfirmDisablePOS]= useState(false);
  const [confirmDisableQR, setConfirmDisableQR] = useState(false);
  const [toast,            setToast]            = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const applyPosToggle = async (newEnabled) => {
    setConfirmDisablePOS(false);
    setPosSaving(true);
    const { error } = await adminService.updatePosEnabled(restaurant.id, newEnabled);
    setPosSaving(false);
    if (error) {
      showToast('Failed to update Menuron Lite: ' + (error.message || ''), 'error');
    } else {
      setPosEnabled(newEnabled);
      onPosToggled?.(restaurant.id, newEnabled);
      showToast(newEnabled ? 'Menuron Lite enabled.' : 'Menuron Lite disabled.', 'success');
      // QR App depends on Menuron Lite — auto-disable QR when Lite is turned off
      if (!newEnabled && qrAppEnabled) {
        await adminService.updateQrAppEnabled(restaurant.id, false);
        setQrAppEnabled(false);
      }
    }
  };

  const applyQrToggle = async (newEnabled) => {
    setConfirmDisableQR(false);
    setQrSaving(true);
    const { error } = await adminService.updateQrAppEnabled(restaurant.id, newEnabled);
    setQrSaving(false);
    if (error) {
      showToast('Failed to update QR App: ' + (error.message || ''), 'error');
    } else {
      setQrAppEnabled(newEnabled);
      showToast(newEnabled ? 'QR App enabled.' : 'QR App disabled.', 'success');
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: T.bg,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex', flexDirection: 'column',
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} />}

      {/* Top header bar */}
      <header style={{
        background: T.surface, borderBottom: `1px solid ${T.border}`,
        padding: '0 24px', height: '56px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}>
        {/* Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
          <button
            onClick={onBack}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: T.muted, fontWeight: '600', fontSize: '13px', padding: '4px 0',
            }}
          >← Dashboard</button>
          <span style={{ color: T.border }}>/</span>
          <span style={{ fontWeight: '700', color: T.text }}>{restaurant.name}</span>
        </div>

        {/* Status + Lite toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{
            padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
            background: restaurant.is_active ? T.greenBg : T.redBg,
            color: restaurant.is_active ? T.green : T.red,
            border: `1px solid ${restaurant.is_active ? T.greenBorder : T.redBorder}`,
          }}>
            {restaurant.is_active ? 'ACTIVE' : 'PAUSED'}
          </span>

          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', background: T.bg, borderRadius: '8px', padding: '5px 10px' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: T.muted }}>Menuron Lite</span>
            <button
              onClick={() => posEnabled ? setConfirmDisablePOS(true) : applyPosToggle(true)}
              disabled={posSaving}
              style={{
                position: 'relative', display: 'inline-flex', alignItems: 'center',
                width: '40px', height: '22px', borderRadius: '11px', border: 'none',
                background: posEnabled ? T.green : '#d1d5db',
                cursor: posSaving ? 'not-allowed' : 'pointer',
                padding: 0, transition: 'background 0.25s',
                opacity: posSaving ? 0.5 : 1,
              }}
            >
              <span style={{
                position: 'absolute', top: '2px',
                left: posEnabled ? '20px' : '2px',
                width: '18px', height: '18px', borderRadius: '50%',
                background: 'white', transition: 'left 0.25s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </button>
            <span style={{ fontSize: '11px', fontWeight: '700', color: posEnabled ? T.green : T.muted, minWidth: '22px' }}>
              {posSaving ? '…' : posEnabled ? 'ON' : 'OFF'}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', background: T.bg, borderRadius: '8px', padding: '5px 10px', opacity: posEnabled ? 1 : 0.4 }}
            title={posEnabled ? '' : 'Enable Menuron Lite first'}
          >
            <span style={{ fontSize: '12px', fontWeight: '600', color: T.muted }}>QR App</span>
            <button
              onClick={() => { if (!posEnabled) return; qrAppEnabled ? setConfirmDisableQR(true) : applyQrToggle(true); }}
              disabled={qrSaving || !posEnabled}
              style={{
                position: 'relative', display: 'inline-flex', alignItems: 'center',
                width: '40px', height: '22px', borderRadius: '11px', border: 'none',
                background: qrAppEnabled ? '#7C3AED' : '#d1d5db',
                cursor: (qrSaving || !posEnabled) ? 'not-allowed' : 'pointer',
                padding: 0, transition: 'background 0.25s',
                opacity: qrSaving ? 0.5 : 1,
              }}
            >
              <span style={{
                position: 'absolute', top: '2px',
                left: qrAppEnabled ? '20px' : '2px',
                width: '18px', height: '18px', borderRadius: '50%',
                background: 'white', transition: 'left 0.25s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </button>
            <span style={{ fontSize: '11px', fontWeight: '700', color: qrAppEnabled ? '#7C3AED' : T.muted, minWidth: '22px' }}>
              {qrSaving ? '…' : qrAppEnabled ? 'ON' : 'OFF'}
            </span>
          </div>
        </div>
      </header>

      {/* Body: sidebar + content */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Left sidebar */}
        <aside style={{
          width: T.sidebarW, flexShrink: 0,
          background: T.surface, borderRight: `1px solid ${T.border}`,
          display: 'flex', flexDirection: 'column',
          padding: '24px 16px',
          minHeight: 'calc(100vh - 56px)',
        }}>
          {/* Restaurant avatar + name */}
          <div style={{ marginBottom: '28px', paddingLeft: '4px' }}>
            <div style={{
              width: '44px', height: '44px', borderRadius: '12px',
              background: T.primaryLight, color: T.primary,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '20px', fontWeight: '900', marginBottom: '10px',
            }}>
              {restaurant.name?.[0]?.toUpperCase()}
            </div>
            <div style={{ fontSize: '14px', fontWeight: '800', color: T.text, lineHeight: 1.2 }}>{restaurant.name}</div>
            <div style={{ fontSize: '11px', color: T.muted, marginTop: '3px' }}>
              {restaurant.restaurant_owners?.full_name}
            </div>
          </div>

          {/* Nav items */}
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
            {NAV_ITEMS.map(item => {
              const isActive = activeTab === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => { sessionStorage.setItem('admin_active_tab', item.key); setActiveTab(item.key); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 12px', borderRadius: '10px', border: 'none',
                    cursor: 'pointer', textAlign: 'left', width: '100%',
                    background: isActive ? T.primaryLight : 'transparent',
                    color: isActive ? T.primary : T.muted,
                    fontWeight: isActive ? '700' : '600',
                    fontSize: '14px',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = T.bg; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  {item.label}
                  {isActive && (
                    <span style={{
                      marginLeft: 'auto', width: '6px', height: '6px',
                      borderRadius: '50%', background: T.primary,
                    }} />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Back button at bottom */}
          <button
            onClick={onBack}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 12px', borderRadius: '10px', border: `1.5px solid ${T.border}`,
              cursor: 'pointer', background: 'transparent',
              color: T.muted, fontWeight: '600', fontSize: '13px',
              marginTop: '16px',
            }}
          >← Back to Dashboard</button>
        </aside>

        {/* Main content */}
        <main style={{ flex: 1, padding: '28px', overflowY: 'auto', minHeight: 'calc(100vh - 56px)' }}>
          {activeTab === 'Analytics'       && <AnalyticsDashboard restaurant={restaurant} />}
          {activeTab === 'Invoices'        && <InvoicesApp       restaurant={restaurant} />}
          {activeTab === 'Reports'         && <ReportsApp        restaurant={restaurant} userType="owner" />}
          {activeTab === 'Menu'            && <MenuTab           restaurant={restaurant} />}
          {activeTab === 'Tables'          && <TablesTab         restaurant={restaurant} />}
          {activeTab === 'Orders'          && <OrdersTab         restaurant={restaurant} />}
          {activeTab === 'Waiters'         && <WaitersTab        restaurant={restaurant} />}
          {activeTab === 'Customers'       && <CRMApp            restaurant={restaurant} />}
          {activeTab === 'Inventory'       && <InventoryTab      restaurant={restaurant} />}
          {activeTab === 'BillingSettings' && <BillingSettings   restaurant={restaurant} />}
          {activeTab === 'Profile'         && <ProfileTab        restaurant={restaurant} showToast={showToast} onSaved={setRestaurant} />}
        </main>
      </div>

      {/* Confirm disable Menuron Lite modal */}
      {confirmDisablePOS && (
        <Overlay>
          <ModalCard>
            <h3 style={ms.modalTitle}>Disable Menuron Lite?</h3>
            <p style={ms.modalSubtitle}>
              This will restore full web and desktop access for <strong>{restaurant.name}</strong>.
            </p>
            <div style={ms.actions}>
              <button style={ms.cancelBtn} onClick={() => setConfirmDisablePOS(false)}>Cancel</button>
              <button style={{ ...ms.confirmBtn, background: T.red }} onClick={() => applyPosToggle(false)}>
                Disable Menuron Lite
              </button>
            </div>
          </ModalCard>
        </Overlay>
      )}

      {confirmDisableQR && (
        <Overlay>
          <ModalCard>
            <h3 style={ms.modalTitle}>Disable QR App?</h3>
            <p style={ms.modalSubtitle}>
              Customers will no longer be able to place orders via QR codes for <strong>{restaurant.name}</strong>.
            </p>
            <div style={ms.actions}>
              <button style={ms.cancelBtn} onClick={() => setConfirmDisableQR(false)}>Cancel</button>
              <button style={{ ...ms.confirmBtn, background: T.red }} onClick={() => applyQrToggle(false)}>
                Disable QR App
              </button>
            </div>
          </ModalCard>
        </Overlay>
      )}
    </div>
  );
};

// ── MENU TAB ───────────────────────────────────────────────────
const EMPTY_ITEM = { name: '', description: '', price: '', category_id: '', is_available: true, image_url: '', food_type: 'veg' };

const MenuTab = ({ restaurant }) => {
  const [categories,       setCategories]       = useState([]);
  const [menuItems,        setMenuItems]        = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [loadError,        setLoadError]        = useState('');
  const [showCategoryModal,setShowCategoryModal]= useState(false);
  const [showItemModal,    setShowItemModal]    = useState(false);
  const [editItem,         setEditItem]         = useState(null);
  const [newCategoryName,  setNewCategoryName]  = useState('');
  const [itemForm, setItemForm] = useState(EMPTY_ITEM);
  const [uploading, setUploading] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});
  const fileInputRef = useRef(null);

  const toggleCat = (catId) => setExpandedCats(prev => ({ ...prev, [catId]: prev[catId] === false ? true : false }));

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    setLoadError('');
    const [catRes, itemRes] = await Promise.all([
      adminService.getRestaurantCategories(restaurant.id),
      adminService.getMenuItems(restaurant.id),
    ]);
    if (catRes.error || itemRes.error) {
      setLoadError((catRes.error || itemRes.error).message || 'Failed to load menu data');
    }
    setCategories(catRes.data || []);
    setMenuItems(itemRes.data || []);
    setLoading(false);
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    await adminService.createCategory(restaurant.id, newCategoryName.trim(), categories.length + 1);
    setNewCategoryName('');
    setShowCategoryModal(false);
    loadData();
  };

  const handleDeleteCategory = async (catId, catName) => {
    if (!window.confirm(`Delete category "${catName}"? All items in this category will also be deleted.`)) return;
    await adminService.deleteCategory(catId);
    loadData();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const { url, error } = await adminService.uploadMenuItemImage(file, restaurant.id);
    if (url) setItemForm(f => ({ ...f, image_url: url }));
    else alert('Image upload failed: ' + (error?.message || 'Unknown error'));
    setUploading(false);
  };

  const handleSaveItem = async () => {
    if (!itemForm.name.trim() || !itemForm.price || !itemForm.category_id) {
      alert('Name, price and category are required'); return;
    }
    const data = {
      name: itemForm.name.trim(),
      description: itemForm.description,
      price: parseFloat(itemForm.price),
      category_id: itemForm.category_id,
      is_available: itemForm.is_available,
      image_url: itemForm.image_url,
      food_type: itemForm.food_type,
      restaurant_id: restaurant.id,
    };
    if (editItem) await adminService.updateMenuItem(editItem.id, data);
    else          await adminService.createMenuItem(data);
    setItemForm(EMPTY_ITEM);
    setEditItem(null);
    setShowItemModal(false);
    loadData();
  };

  const openEdit = (item) => {
    setItemForm({
      name: item.name,
      description: item.description || '',
      price: item.price,
      category_id: item.category_id,
      is_available: item.is_available,
      image_url: item.image_url || '',
      food_type: item.food_type || 'veg',
    });
    setEditItem(item);
    setShowItemModal(true);
  };

  const handleDeleteItem = async (itemId, itemName) => {
    if (!window.confirm(`Delete "${itemName}"?`)) return;
    await adminService.deleteMenuItem(itemId);
    loadData();
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader title="Menu" subtitle={`${categories.length} categories · ${menuItems.length} items`}>
        <ActionBtn onClick={() => setShowCategoryModal(true)}>+ Category</ActionBtn>
        <ActionBtn primary onClick={() => { setEditItem(null); setItemForm(EMPTY_ITEM); setShowItemModal(true); }}>
          + Add Item
        </ActionBtn>
      </PageHeader>

      {loadError && (
        <div style={{ background: T.redBg, border: `1px solid ${T.redBorder}`, borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', color: T.red, fontSize: '13px', fontWeight: '600' }}>
          Error loading menu: {loadError}
        </div>
      )}

      {/* Collapsible category sections */}
      {categories.length === 0 && menuItems.length === 0 ? (
        <Panel>
          <EmptyState title="No Menu Yet" desc='Click "+ Category" to add your first category, then add items under it.' />
        </Panel>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {categories.map(cat => {
            const catItems = menuItems.filter(i => i.category_id === cat.id);
            const isOpen = expandedCats[cat.id] !== false;
            return (
              <div key={cat.id} style={{ background: T.surface, borderRadius: '14px', border: `1px solid ${T.border}`, overflow: 'hidden' }}>

                {/* Category header */}
                <div
                  onClick={() => toggleCat(cat.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '14px 18px', cursor: 'pointer',
                    background: isOpen ? T.primaryLight : T.surface,
                    borderBottom: isOpen ? `1px solid ${T.border}` : 'none',
                    transition: 'background 0.15s',
                  }}
                >
                  <span style={{ fontSize: '16px', flex: 1, fontWeight: '800', color: T.text }}>{cat.name}</span>
                  <span style={{
                    fontSize: '11px', fontWeight: '700', padding: '2px 9px',
                    borderRadius: '10px', background: T.primary, color: 'white',
                  }}>
                    {catItems.length} item{catItems.length !== 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={e => { e.stopPropagation(); handleDeleteCategory(cat.id, cat.name); }}
                    style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: '2px 4px' }}
                  >×</button>
                  <span style={{ color: T.muted, fontSize: '12px', fontWeight: '700', minWidth: '16px', textAlign: 'center' }}>
                    {isOpen ? '▲' : '▼'}
                  </span>
                </div>

                {/* Items list */}
                {isOpen && (
                  <div>
                    {catItems.length === 0 ? (
                      <div style={{ padding: '18px 20px', fontSize: '13px', color: T.muted, fontStyle: 'italic' }}>
                        No items in this category yet. Click "+ Add Item" and select "{cat.name}".
                      </div>
                    ) : (
                      catItems.map((item, idx) => (
                        <div key={item.id} style={{
                          display: 'flex', alignItems: 'center', gap: '14px',
                          padding: '12px 18px', background: T.surface,
                          borderTop: idx > 0 ? `1px solid ${T.border}` : 'none',
                        }}>
                          {item.image_url && (
                            <img src={item.image_url} alt={item.name} style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '10px', flexShrink: 0 }} />
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontSize: '12px' }}>{item.food_type === 'non_veg' ? '🔴' : '🟢'}</span>
                              <span style={{ fontSize: '14px', fontWeight: '700', color: T.text }}>{item.name}</span>
                            </div>
                            <div style={{ fontSize: '12px', color: T.muted, marginTop: '2px' }}>
                              {restaurant.default_currency || '₹'}{item.price}
                              {item.description && ` · ${item.description}`}
                            </div>
                          </div>
                          <span style={{
                            padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
                            background: item.is_available ? T.greenBg : T.redBg,
                            color: item.is_available ? T.green : T.red,
                            border: `1px solid ${item.is_available ? T.greenBorder : T.redBorder}`,
                            flexShrink: 0,
                          }}>
                            {item.is_available ? 'Available' : 'Unavailable'}
                          </span>
                          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                            <SmBtn label="Edit"   color={T.primary} onClick={() => openEdit(item)} />
                            <SmBtn label="Delete" color={T.red}     onClick={() => handleDeleteItem(item.id, item.name)} />
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Category Modal */}
      {showCategoryModal && (
        <Overlay>
          <ModalCard>
            <h3 style={ms.modalTitle}>Add Category</h3>
            <input style={{ ...ms.input, marginBottom: '24px' }}
              placeholder="Category name *"
              value={newCategoryName}
              onChange={e => setNewCategoryName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
              autoFocus
            />
            <div style={ms.actions}>
              <button style={ms.cancelBtn} onClick={() => { setShowCategoryModal(false); setNewCategoryName(''); }}>Cancel</button>
              <button style={{ ...ms.confirmBtn, background: T.gradient }} onClick={handleAddCategory}>Add Category</button>
            </div>
          </ModalCard>
        </Overlay>
      )}

      {/* Add / Edit Item Modal */}
      {showItemModal && (
        <Overlay>
          <ModalCard wide>
            <h3 style={ms.modalTitle}>{editItem ? 'Edit Item' : 'Add Menu Item'}</h3>
            <div style={{ display: 'grid', gap: '14px', marginBottom: '24px' }}>
              <div>
                <label style={ms.label}>Item Name *</label>
                <input style={ms.input} placeholder="e.g. Butter Chicken" value={itemForm.name}
                  onChange={e => setItemForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label style={ms.label}>Description</label>
                <textarea style={{ ...ms.input, resize: 'vertical' }} placeholder="Short description (optional)"
                  value={itemForm.description} onChange={e => setItemForm(f => ({ ...f, description: e.target.value }))} rows={2} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={ms.label}>Price *</label>
                  <input style={ms.input} placeholder="0.00" type="number" step="0.01" value={itemForm.price}
                    onChange={e => setItemForm(f => ({ ...f, price: e.target.value }))} />
                </div>
                <div>
                  <label style={ms.label}>Currency</label>
                  <input style={{ ...ms.input, background: T.bg, color: T.muted, cursor: 'not-allowed' }}
                    value={restaurant.default_currency || '₹'} disabled />
                </div>
              </div>
              <div>
                <label style={ms.label}>Category *</label>
                <select style={ms.input} value={itemForm.category_id}
                  onChange={e => setItemForm(f => ({ ...f, category_id: e.target.value }))}>
                  <option value="">Select a category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={ms.label}>Food Type</label>
                <select style={ms.input} value={itemForm.food_type}
                  onChange={e => setItemForm(f => ({ ...f, food_type: e.target.value }))}>
                  <option value="veg">🟢 Vegetarian</option>
                  <option value="non_veg">🔴 Non-Vegetarian</option>
                </select>
              </div>
              <div>
                <label style={ms.label}>Item Image</label>
                {itemForm.image_url && (
                  <img src={itemForm.image_url} alt="preview"
                    style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '10px', marginBottom: '10px', display: 'block' }} />
                )}
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  style={{ ...ms.cancelBtn, fontSize: '12px', padding: '7px 14px' }}>
                  {uploading ? 'Uploading…' : itemForm.image_url ? 'Change Image' : 'Upload Image'}
                </button>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input type="checkbox" checked={itemForm.is_available}
                  onChange={e => setItemForm(f => ({ ...f, is_available: e.target.checked }))}
                  style={{ width: '16px', height: '16px', accentColor: T.primary }} />
                <span style={{ fontSize: '13px', fontWeight: '600', color: T.text }}>Available for ordering</span>
              </label>
            </div>
            <div style={ms.actions}>
              <button style={ms.cancelBtn} onClick={() => { setShowItemModal(false); setEditItem(null); setItemForm(EMPTY_ITEM); }}>Cancel</button>
              <button style={{ ...ms.confirmBtn, background: T.gradient }} onClick={handleSaveItem}>
                {editItem ? 'Update Item' : 'Add Item'}
              </button>
            </div>
          </ModalCard>
        </Overlay>
      )}
    </div>
  );
};

// ── TABLES TAB ─────────────────────────────────────────────────
const TablesTab = ({ restaurant }) => {
  const [tables,    setTables]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [tableForm, setTableForm] = useState({ table_number: '', capacity: '' });
  const [editTable, setEditTable] = useState(null);
  const canvasRefs = useRef({});

  useEffect(() => { loadTables(); }, []);

  const loadTables = async () => {
    setLoading(true);
    const { data } = await adminService.getRestaurantTables(restaurant.id);
    setTables(data || []);
    setLoading(false);
  };

  useEffect(() => { tables.forEach(t => generateQR(t)); }, [tables]);

  const generateQR = (table) => {
    const canvas = canvasRefs.current[table.id];
    if (!canvas) return;
    const qrUrl = `${window.location.origin}/menu/${table.qr_code}`;
    import('qrcode').then(QRCode => {
      QRCode.toCanvas(canvas, qrUrl, { width: 90, margin: 1 }, () => {});
    }).catch(() => {});
  };

  const handleSaveTable = async () => {
    if (!tableForm.table_number) { alert('Table number is required'); return; }
    const data = {
      table_number: parseInt(tableForm.table_number),
      capacity: parseInt(tableForm.capacity) || 4,
      qr_code: `${restaurant.id}-table-${tableForm.table_number}-${Date.now()}`,
    };
    if (editTable) await adminService.updateTable(editTable.id, { table_number: data.table_number, capacity: data.capacity });
    else           await adminService.createTable(restaurant.id, data);
    setTableForm({ table_number: '', capacity: '' });
    setEditTable(null);
    setShowModal(false);
    loadTables();
  };

  const handleDeleteTable = async (tableId, tableNumber) => {
    if (!window.confirm(`Delete Table ${tableNumber}?`)) return;
    await adminService.deleteTable(tableId);
    loadTables();
  };

  const downloadQR = (table) => {
    const canvas = canvasRefs.current[table.id];
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `table-${table.table_number}-qr.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader title="Tables" subtitle={`${tables.length} tables`}>
        <ActionBtn primary onClick={() => { setEditTable(null); setTableForm({ table_number: '', capacity: '' }); setShowModal(true); }}>
          + Add Table
        </ActionBtn>
      </PageHeader>

      <Panel>
        {tables.length === 0 ? (
          <EmptyState title="No Tables" desc='Click "+ Add Table" to create your first table.' />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>
            {tables.map(table => (
              <div key={table.id} style={{
                background: T.bg, borderRadius: '14px', padding: '16px',
                border: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: '12px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: '800', color: T.text }}>Table {table.table_number}</div>
                    <div style={{ fontSize: '12px', color: T.muted, marginTop: '2px' }}>Capacity: {table.capacity} seats</div>
                  </div>
                  <span style={{
                    padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
                    background: T.greenBg, color: T.green, border: `1px solid ${T.greenBorder}`,
                  }}>Active</span>
                </div>

                <canvas
                  ref={el => { canvasRefs.current[table.id] = el; if (el) generateQR(table); }}
                  style={{ borderRadius: '8px', border: `1px solid ${T.border}`, alignSelf: 'center' }}
                />

                <div style={{ display: 'flex', gap: '8px' }}>
                  <SmBtn label="Download QR" color={T.primary} onClick={() => downloadQR(table)} full />
                  <SmBtn label="Edit" color={T.orange} onClick={() => { setEditTable(table); setTableForm({ table_number: table.table_number, capacity: table.capacity }); setShowModal(true); }} />
                  <SmBtn label="Delete" color={T.red} onClick={() => handleDeleteTable(table.id, table.table_number)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {showModal && (
        <Overlay>
          <ModalCard>
            <h3 style={ms.modalTitle}>{editTable ? 'Edit Table' : 'Add Table'}</h3>
            <div style={{ display: 'grid', gap: '12px', marginBottom: '24px' }}>
              <input style={ms.input} placeholder="Table Number *" type="number"
                value={tableForm.table_number} onChange={e => setTableForm(f => ({ ...f, table_number: e.target.value }))} />
              <input style={ms.input} placeholder="Capacity (default: 4)" type="number"
                value={tableForm.capacity} onChange={e => setTableForm(f => ({ ...f, capacity: e.target.value }))} />
            </div>
            <div style={ms.actions}>
              <button style={ms.cancelBtn} onClick={() => { setShowModal(false); setEditTable(null); }}>Cancel</button>
              <button style={{ ...ms.confirmBtn, background: T.gradient }} onClick={handleSaveTable}>
                {editTable ? 'Update Table' : 'Add Table'}
              </button>
            </div>
          </ModalCard>
        </Overlay>
      )}
    </div>
  );
};

// ── ORDERS TAB ─────────────────────────────────────────────────
const OrdersTab = ({ restaurant }) => {
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadOrders(); }, []);

  const loadOrders = async () => {
    setLoading(true);
    const { data } = await adminService.getRestaurantOrders(restaurant.id);
    setOrders(data || []);
    setLoading(false);
  };

  const STATUS = {
    pending:   { bg: '#fffbeb', border: '#fde68a', color: '#b45309', label: 'Pending' },
    confirmed: { bg: '#eff6ff', border: '#bfdbfe', color: '#1d4ed8', label: 'Confirmed' },
    preparing: { bg: '#eff6ff', border: '#bfdbfe', color: '#1d4ed8', label: 'Preparing' },
    ready:     { bg: '#f5f3ff', border: '#ddd6fe', color: '#7c3aed', label: 'Ready' },
    delivered: { bg: T.greenBg, border: T.greenBorder, color: T.green, label: 'Delivered' },
    billed:    { bg: T.greenBg, border: T.greenBorder, color: T.green, label: 'Billed' },
    cancelled: { bg: T.redBg,   border: T.redBorder,   color: T.red,   label: 'Cancelled' },
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader title="Orders" subtitle={`${orders.length} total`} />
      <Panel>
        {orders.length === 0 ? (
          <EmptyState title="No Orders" desc="Orders will appear here once customers start ordering." />
        ) : (
          <div style={{ display: 'grid', gap: '1px', background: T.border, borderRadius: '12px', overflow: 'hidden' }}>
            {orders.map(order => {
              const cfg = STATUS[order.status] || { bg: T.bg, border: T.border, color: T.muted, label: order.status };
              return (
                <div key={order.id} style={{
                  display: 'flex', alignItems: 'center', gap: '14px',
                  padding: '14px 16px', background: T.surface,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: T.text }}>
                      {order.order_number ? `#${order.order_number}` : 'Order'} · Table {order.restaurant_tables?.table_number || 'N/A'}
                    </div>
                    <div style={{ fontSize: '12px', color: T.muted, marginTop: '2px' }}>
                      {order.order_items?.map(i => `${i.quantity}× ${i.menu_items?.name}`).join(', ')}
                    </div>
                    <div style={{ fontSize: '11px', color: T.muted, marginTop: '2px' }}>
                      {new Date(order.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  <div style={{ fontWeight: '800', color: T.primary, fontSize: '14px' }}>
                    {restaurant.default_currency}{order.total_amount || order.total_payable}
                  </div>
                  <span style={{
                    padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
                    background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                  }}>
                    {cfg.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
};

// ── WAITERS TAB ────────────────────────────────────────────────
const WaitersTab = ({ restaurant }) => {
  const [waiters,   setWaiters]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [editWaiter,setEditWaiter]= useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '' });

  useEffect(() => { loadWaiters(); }, []);

  const loadWaiters = async () => {
    setLoading(true);
    const { data } = await adminService.getRestaurantWaiters(restaurant.id);
    setWaiters(data || []);
    setLoading(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { alert('Name is required'); return; }
    if (!editWaiter && !form.password) { alert('Password is required for new waiters'); return; }
    if (editWaiter) await adminService.updateWaiter(editWaiter.id, form);
    else            await adminService.createWaiter(restaurant.id, form);
    setForm({ name: '', email: '', password: '' });
    setEditWaiter(null);
    setShowForm(false);
    loadWaiters();
  };

  const handleDelete = async (waiterId, waiterName) => {
    if (!window.confirm(`Delete waiter "${waiterName}"?`)) return;
    await adminService.deleteWaiter(waiterId);
    loadWaiters();
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader title="Waiters" subtitle={`${waiters.length} staff`}>
        <ActionBtn primary onClick={() => { setEditWaiter(null); setForm({ name: '', email: '', password: '' }); setShowForm(true); }}>
          + Add Waiter
        </ActionBtn>
      </PageHeader>

      <Panel>
        {waiters.length === 0 ? (
          <EmptyState title="No Waiters" desc='Click "+ Add Waiter" to add your first staff member.' />
        ) : (
          <div style={{ display: 'grid', gap: '1px', background: T.border, borderRadius: '12px', overflow: 'hidden' }}>
            {waiters.map(waiter => (
              <div key={waiter.id} style={{
                display: 'flex', alignItems: 'center', gap: '14px',
                padding: '14px 16px', background: T.surface,
              }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '50%',
                  background: T.primaryLight, color: T.primary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '15px', fontWeight: '800', flexShrink: 0,
                }}>
                  {waiter.name?.[0]?.toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: T.text }}>{waiter.name}</div>
                  <div style={{ fontSize: '12px', color: T.muted, marginTop: '2px' }}>{waiter.email || 'No email on file'}</div>
                </div>
                <span style={{
                  padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
                  background: T.greenBg, color: T.green, border: `1px solid ${T.greenBorder}`,
                }}>Active</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <SmBtn label="Edit" color={T.primary} onClick={() => { setEditWaiter(waiter); setForm({ name: waiter.name, email: waiter.email || '', password: '' }); setShowForm(true); }} />
                  <SmBtn label="Delete" color={T.red} onClick={() => handleDelete(waiter.id, waiter.name)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {showForm && (
        <Overlay>
          <ModalCard>
            <h3 style={ms.modalTitle}>{editWaiter ? 'Edit Waiter' : 'Add Waiter'}</h3>
            <div style={{ display: 'grid', gap: '12px', marginBottom: '24px' }}>
              <input style={ms.input} placeholder="Full Name *" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              <input style={ms.input} placeholder="Email (optional)" type="email" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              <input style={ms.input} type="password" value={form.password}
                placeholder={editWaiter ? 'New Password (leave blank to keep)' : 'Password *'}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            </div>
            <div style={ms.actions}>
              <button style={ms.cancelBtn} onClick={() => { setShowForm(false); setEditWaiter(null); }}>Cancel</button>
              <button style={{ ...ms.confirmBtn, background: T.gradient }} onClick={handleSave}>
                {editWaiter ? 'Update Waiter' : 'Add Waiter'}
              </button>
            </div>
          </ModalCard>
        </Overlay>
      )}
    </div>
  );
};

// ── Shared layout components ───────────────────────────────────
const PageHeader = ({ title, subtitle, children }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '20px',
  }}>
    <div>
      <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: T.text }}>{title}</h2>
      {subtitle && <p style={{ margin: '3px 0 0', fontSize: '13px', color: T.muted }}>{subtitle}</p>}
    </div>
    {children && <div style={{ display: 'flex', gap: '10px' }}>{children}</div>}
  </div>
);

const ActionBtn = ({ children, onClick, primary }) => (
  <button onClick={onClick} style={{
    background: primary ? T.gradient : 'transparent',
    color: primary ? 'white' : T.primary,
    border: primary ? 'none' : `1.5px solid ${T.primary}`,
    padding: '9px 16px', borderRadius: '8px',
    fontSize: '13px', fontWeight: '700', cursor: 'pointer',
    boxShadow: primary ? '0 2px 8px rgba(255,68,68,0.25)' : 'none',
  }}>{children}</button>
);

const Panel = ({ children }) => (
  <div style={{
    background: T.surface, borderRadius: '14px',
    border: `1px solid ${T.border}`,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    overflow: 'hidden', padding: '0',
  }}>{children}</div>
);

const EmptyState = ({ title, desc }) => (
  <div style={{ textAlign: 'center', padding: '60px 40px', color: T.muted }}>
    <h3 style={{ color: T.text, margin: '0 0 6px', fontSize: '15px' }}>{title}</h3>
    <p style={{ margin: 0, fontSize: '13px' }}>{desc}</p>
  </div>
);

const SmBtn = ({ label, color, onClick, full }) => (
  <button onClick={onClick} style={{
    background: 'transparent', color, border: `1.5px solid ${color}`,
    padding: '5px 11px', borderRadius: '7px', cursor: 'pointer',
    fontSize: '12px', fontWeight: '700',
    flex: full ? 1 : undefined,
  }}
    onMouseEnter={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = 'white'; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = color; }}
  >{label}</button>
);

const Toast = ({ message, type }) => (
  <div style={{
    position: 'fixed', top: '16px', right: '16px', zIndex: 9999,
    background: type === 'success' ? T.greenBg : T.redBg,
    border: `1px solid ${type === 'success' ? T.greenBorder : T.redBorder}`,
    color: type === 'success' ? T.green : T.red,
    borderRadius: '10px', padding: '12px 18px',
    fontSize: '13px', fontWeight: '600',
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)', maxWidth: '360px',
  }}>{message}</div>
);

const LoadingSpinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
    <div style={{
      width: '36px', height: '36px', borderRadius: '50%',
      border: `3px solid ${T.primaryLight}`, borderTopColor: T.primary,
      animation: 'spin 0.8s linear infinite',
    }} />
  </div>
);

const Overlay = ({ children }) => (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, backdropFilter: 'blur(4px)',
  }}>{children}</div>
);

const ModalCard = ({ children, wide }) => (
  <div style={{
    background: T.surface, borderRadius: '20px', padding: '28px',
    width: '90%', maxWidth: wide ? '560px' : '440px',
    maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
  }}>{children}</div>
);

const ms = {
  modalTitle:  { margin: '0 0 6px', fontSize: '16px', fontWeight: '800', color: T.text },
  modalSubtitle:{ margin: '0 0 20px', fontSize: '13px', color: T.muted },
  label:       { display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '700', color: T.text },
  input:       { width: '100%', padding: '10px 12px', border: `1.5px solid ${T.border}`, borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', outline: 'none', color: T.text, background: T.surface },
  actions:     { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
  cancelBtn:   { padding: '9px 18px', border: `1.5px solid ${T.border}`, background: 'transparent', color: T.muted, borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  confirmBtn:  { padding: '9px 18px', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' },
};

// ── Profile Tab ──────────────────────────────────────────────────────────────

const TIMEZONES = [
  { value: 'Asia/Kolkata',       label: 'Asia/Kolkata (IST +5:30)' },
  { value: 'Asia/Dubai',         label: 'Asia/Dubai (GST +4:00)' },
  { value: 'Asia/Singapore',     label: 'Asia/Singapore (SGT +8:00)' },
  { value: 'Asia/Kuala_Lumpur',  label: 'Asia/Kuala_Lumpur (MYT +8:00)' },
  { value: 'Asia/Bangkok',       label: 'Asia/Bangkok (ICT +7:00)' },
  { value: 'Asia/Jakarta',       label: 'Asia/Jakarta (WIB +7:00)' },
  { value: 'Asia/Manila',        label: 'Asia/Manila (PST +8:00)' },
  { value: 'Asia/Colombo',       label: 'Asia/Colombo (SLST +5:30)' },
  { value: 'Asia/Dhaka',         label: 'Asia/Dhaka (BST +6:00)' },
  { value: 'Asia/Karachi',       label: 'Asia/Karachi (PKT +5:00)' },
  { value: 'Europe/London',      label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Paris',       label: 'Europe/Paris (CET +1:00)' },
  { value: 'America/New_York',   label: 'America/New_York (ET)' },
  { value: 'America/Chicago',    label: 'America/Chicago (CT)' },
  { value: 'America/Denver',     label: 'America/Denver (MT)' },
  { value: 'America/Los_Angeles',label: 'America/Los_Angeles (PT)' },
  { value: 'Australia/Sydney',   label: 'Australia/Sydney (AEDT +11:00)' },
  { value: 'Pacific/Auckland',   label: 'Pacific/Auckland (NZDT +13:00)' },
];

const CURRENCIES = ['₹', '$', '£', '€', 'AED', 'S$', 'A$', 'C$'];

const ProfileTab = ({ restaurant, showToast, onSaved }) => {
  const owner = restaurant.restaurant_owners || {};

  const [form, setForm] = useState({
    name:             restaurant.name             || '',
    address:          restaurant.address          || '',
    tagline:          restaurant.tagline          || '',
    contact_email:    restaurant.contact_email    || '',
    contact_phone:    restaurant.contact_phone    || '',
    default_currency: restaurant.default_currency || '₹',
    timezone:         restaurant.timezone         || 'Asia/Kolkata',
  });
  const [ownerForm, setOwnerForm] = useState({
    fullName: owner.full_name || '',
    email:    owner.email    || '',
  });
  const [logoPreview,   setLogoPreview]   = useState(restaurant.logo_url || null);
  const [logoFile,      setLogoFile]      = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [ownerSaving,   setOwnerSaving]   = useState(false);
  const [errors,        setErrors]        = useState({});
  const [ownerErrors,   setOwnerErrors]   = useState({});

  const field = (key, e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const oField = (key, e) => setOwnerForm(f => ({ ...f, [key]: e.target.value }));

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    let logo_url = restaurant.logo_url || undefined;
    if (logoFile) {
      const { url, error: uploadErr } = await adminService.uploadRestaurantLogo(logoFile, restaurant.id);
      if (uploadErr) { showToast('Logo upload failed: ' + (uploadErr.message || ''), 'error'); setSaving(false); return; }
      logo_url = url;
    }
    const updates = { ...form };
    if (logo_url !== undefined) updates.logo_url = logo_url;
    const { data, error } = await adminService.updateRestaurant(restaurant.id, updates);
    setSaving(false);
    if (error) showToast('Failed to save: ' + (error.message || ''), 'error');
    else {
      showToast('Profile updated.', 'success');
      setLogoFile(null);
      if (data) onSaved(prev => ({ ...prev, ...data }));
    }
  };

  const handleOwnerSave = async () => {
    const e = {};
    if (!ownerForm.fullName.trim()) e.fullName = 'Required';
    if (ownerForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerForm.email)) e.email = 'Invalid email';
    setOwnerErrors(e);
    if (Object.keys(e).length > 0) return;
    setOwnerSaving(true);
    const { error } = await adminService.updateOwnerProfile(restaurant.id, ownerForm);
    setOwnerSaving(false);
    if (error) showToast('Failed to update owner: ' + (error.message || ''), 'error');
    else {
      showToast('Owner updated.', 'success');
      onSaved(prev => ({
        ...prev,
        restaurant_owners: { ...prev.restaurant_owners, full_name: ownerForm.fullName, email: ownerForm.email },
      }));
    }
  };

  const inp = (hasErr) => ({
    width: '100%', padding: '10px 12px', boxSizing: 'border-box',
    border: `1.5px solid ${hasErr ? T.red : T.border}`, borderRadius: '8px',
    fontSize: '14px', outline: 'none', color: T.text, background: T.surface,
  });
  const lbl   = { display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '700', color: T.text };
  const errSt = { fontSize: '11px', color: T.red, marginTop: '3px' };
  const sec   = { fontSize: '13px', fontWeight: '800', color: T.text, margin: '28px 0 16px', paddingBottom: '8px', borderBottom: `2px solid ${T.border}` };
  const saveBtn = (loading, label) => ({
    marginTop: '20px', padding: '11px 28px', background: T.gradient, color: 'white',
    border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '700',
    cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
  });

  return (
    <div style={{ padding: '28px', maxWidth: '580px' }}>

      {/* ── Restaurant Info ── */}
      <h3 style={sec}>Restaurant Info</h3>
      <div style={{ display: 'grid', gap: '16px' }}>

        {/* Logo */}
        <div>
          <label style={lbl}>Logo</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {logoPreview
              ? <img src={logoPreview} alt="logo" style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '10px', border: `1px solid ${T.border}` }} />
              : <div style={{ width: '72px', height: '72px', borderRadius: '10px', border: `1.5px dashed ${T.border}`, background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', color: T.muted }}>+</div>
            }
            <label style={{ padding: '8px 16px', border: `1.5px solid ${T.primary}`, borderRadius: '8px', color: T.primary, fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
              {logoPreview ? 'Change Logo' : 'Upload Logo'}
              <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleLogoChange} />
            </label>
          </div>
        </div>

        <div>
          <label style={lbl}>Restaurant Name *</label>
          <input style={inp(errors.name)} value={form.name} onChange={e => field('name', e)} />
          {errors.name && <p style={errSt}>{errors.name}</p>}
        </div>
        <div>
          <label style={lbl}>Tagline</label>
          <input style={inp(false)} placeholder="e.g. Taste the Tradition" value={form.tagline} onChange={e => field('tagline', e)} />
        </div>
        <div>
          <label style={lbl}>Address</label>
          <textarea style={{ ...inp(false), resize: 'vertical' }} rows={2} value={form.address} onChange={e => field('address', e)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={lbl}>Contact Email</label>
            <input type="email" style={inp(false)} value={form.contact_email} onChange={e => field('contact_email', e)} />
          </div>
          <div>
            <label style={lbl}>Contact Phone</label>
            <input type="tel" style={inp(false)} value={form.contact_phone} onChange={e => field('contact_phone', e)} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={lbl}>Default Currency</label>
            <select style={inp(false)} value={form.default_currency} onChange={e => field('default_currency', e)}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Timezone</label>
            <select style={inp(false)} value={form.timezone} onChange={e => field('timezone', e)}>
              {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
            </select>
          </div>
        </div>
      </div>
      <button onClick={handleSave} disabled={saving} style={saveBtn(saving, '')}>
        {saving ? 'Saving…' : 'Save Restaurant Info'}
      </button>

      {/* ── Owner Information ── */}
      <h3 style={sec}>Owner Information</h3>
      <div style={{ display: 'grid', gap: '16px' }}>
        <div>
          <label style={lbl}>Name *</label>
          <input style={inp(ownerErrors.fullName)} value={ownerForm.fullName} onChange={e => oField('fullName', e)} />
          {ownerErrors.fullName && <p style={errSt}>{ownerErrors.fullName}</p>}
        </div>
        <div>
          <label style={lbl}>Email</label>
          <input type="email" style={inp(ownerErrors.email)} value={ownerForm.email} onChange={e => oField('email', e)} />
          {ownerErrors.email && <p style={errSt}>{ownerErrors.email}</p>}
        </div>
      </div>
      <button onClick={handleOwnerSave} disabled={ownerSaving} style={saveBtn(ownerSaving, '')}>
        {ownerSaving ? 'Saving…' : 'Save Owner Info'}
      </button>

    </div>
  );
};

// ── INVENTORY TAB ──────────────────────────────────────────────
const INV_SUBTABS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'items',     label: 'Items' },
];

const ITEM_TYPE_LABELS = {
  raw_material: 'Raw Material', packing_material: 'Packing',
  finished_good: 'Finished Good', semi_finished: 'Semi-Finished',
};

const STATUS_FILTERS = [
  { key: 'all',          label: 'All' },
  { key: 'active',       label: 'Active' },
  { key: 'low_stock',    label: 'Low Stock' },
  { key: 'out_of_stock', label: 'Out of Stock' },
];

const stockStatus = (item) => {
  if (item.current_stock <= 0) return { label: 'Out of Stock', color: T.red, bg: T.redBg, border: T.redBorder };
  if (item.min_stock_threshold > 0 && item.current_stock <= item.min_stock_threshold)
    return { label: 'Low Stock', color: T.orange, bg: '#fff7ed', border: '#fed7aa' };
  return { label: 'In Stock', color: T.green, bg: T.greenBg, border: T.greenBorder };
};

const EMPTY_INV_FORM = { name: '', item_code: '', item_type: 'raw_material', category_id: '', base_unit_id: '', purchase_unit_id: '', conversion_factor: '1', preferred_supplier_id: '', min_stock_threshold: '0', notes: '' };

const InventoryTab = ({ restaurant }) => {
  const [subTab,        setSubTab]        = useState('dashboard');
  const [summary,       setSummary]       = useState(null);
  const [items,         setItems]         = useState([]);
  const [categories,    setCategories]    = useState([]);
  const [units,         setUnits]         = useState([]);
  const [suppliers,     setSuppliers]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [catFilter,     setCatFilter]     = useState('all');
  const [statusFilter,  setStatusFilter]  = useState('all');
  const [search,        setSearch]        = useState('');
  // edit / add modal
  const [modalItem,     setModalItem]     = useState(null); // null = closed, 'new' = add, object = edit
  const [modalForm,     setModalForm]     = useState(EMPTY_INV_FORM);
  const [saving,        setSaving]        = useState(false);
  const [saveMsg,       setSaveMsg]       = useState('');
  const [deleting,      setDeleting]      = useState(null);
  // opening stock modal
  const [stockModal,    setStockModal]    = useState(null); // null | item object
  const [stockQty,      setStockQty]      = useState('');
  const [stockVal,      setStockVal]      = useState('');
  const [stockUnitMode, setStockUnitMode] = useState('base'); // 'base' | 'purchase'
  const [stockSaving,   setStockSaving]   = useState(false);
  const [stockMsg,      setStockMsg]      = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true); setError('');
    const [sumRes, itemRes, catRes, unitRes, supRes] = await Promise.all([
      inventoryService.getSummary(restaurant.id),
      inventoryService.getItems(restaurant.id),
      inventoryService.getCategories(restaurant.id),
      inventoryService.getUnits(restaurant.id),
      inventoryService.getSuppliers(restaurant.id),
    ]);
    if (sumRes.error || itemRes.error || catRes.error) {
      setError((sumRes.error || itemRes.error || catRes.error).message || 'Failed to load inventory');
    }
    setSummary(sumRes.data);
    setItems(itemRes.data || []);
    setCategories(catRes.data || []);
    setUnits(unitRes.data || []);
    setSuppliers(supRes.data || []);
    setLoading(false);
  };

  const openAdd = () => {
    setModalItem('new');
    setModalForm(EMPTY_INV_FORM);
    setSaveMsg('');
  };

  const openEdit = (item) => {
    setModalItem(item);
    setModalForm({
      name: item.name,
      item_code: item.item_code || '',
      item_type: item.item_type || 'raw_material',
      category_id: item.category_id || '',
      base_unit_id: item.base_unit_id || '',
      purchase_unit_id: item.purchase_unit_id || '',
      conversion_factor: String(parseFloat(item.conversion_factor) || 1),
      preferred_supplier_id: item.preferred_supplier_id || '',
      min_stock_threshold: (() => {
        const hasPU = item.purchase_unit_id && item.purchase_unit_id !== item.base_unit_id;
        const cf = parseFloat(item.conversion_factor) || 1;
        return hasPU ? String((item.min_stock_threshold || 0) / cf) : String(item.min_stock_threshold ?? 0);
      })(),
      notes: item.notes || '',
    });
    setSaveMsg('');
  };

  const handleSave = async () => {
    if (!modalForm.name.trim()) { setSaveMsg('Name is required'); return; }
    setSaving(true); setSaveMsg('');
    const payload = {
      name: modalForm.name.trim(),
      item_code: modalForm.item_code.trim() || null,
      item_type: modalForm.item_type || 'raw_material',
      category_id: modalForm.category_id || null,
      base_unit_id: modalForm.base_unit_id || null,
      purchase_unit_id: modalForm.purchase_unit_id || null,
      conversion_factor: (modalForm.purchase_unit_id && modalForm.purchase_unit_id !== modalForm.base_unit_id) ? (parseFloat(modalForm.conversion_factor) || 1) : 1,
      preferred_supplier_id: modalForm.preferred_supplier_id || null,
      min_stock_threshold: (() => {
        const hasPU = modalForm.purchase_unit_id && modalForm.purchase_unit_id !== modalForm.base_unit_id;
        const cf = parseFloat(modalForm.conversion_factor) || 1;
        return hasPU ? (parseFloat(modalForm.min_stock_threshold) || 0) * cf : (parseFloat(modalForm.min_stock_threshold) || 0);
      })(),
      notes: modalForm.notes.trim() || null,
    };
    let err;
    if (modalItem === 'new') {
      const res = await inventoryService.createItem(restaurant.id, payload);
      err = res.error;
      if (!err && res.data) setItems(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
    } else {
      const res = await inventoryService.updateItem(modalItem.id, payload);
      err = res.error;
      if (!err) setItems(prev => prev.map(i => i.id === modalItem.id ? { ...i, ...payload, category: categories.find(c => c.id === payload.category_id) || i.category, base_unit: units.find(u => u.id === payload.base_unit_id) || i.base_unit, purchase_unit: units.find(u => u.id === payload.purchase_unit_id) || i.purchase_unit, supplier: suppliers.find(s => s.id === payload.preferred_supplier_id) || i.supplier } : i));
    }
    setSaving(false);
    if (err) { setSaveMsg('Error: ' + (err.message || 'Save failed')); return; }
    setModalItem(null);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This will remove all stock movements, wastage and recipe entries for this item. This cannot be undone.`)) return;
    setDeleting(item.id);
    const { error: err } = await inventoryService.deleteItem(item.id);
    setDeleting(null);
    if (err) { alert('Delete failed: ' + (err.message || '')); return; }
    setItems(prev => prev.filter(i => i.id !== item.id));
  };

  const openStockModal = (item) => {
    setStockModal(item);
    setStockQty('');
    setStockVal('');
    setStockUnitMode(item.purchase_unit_id && item.purchase_unit_id !== item.base_unit_id ? 'purchase' : 'base');
    setStockMsg('');
  };

  const handleSetOpeningStock = async () => {
    const qty = parseFloat(stockQty);
    if (isNaN(qty) || qty < 0) { setStockMsg('Enter a valid quantity'); return; }
    setStockSaving(true); setStockMsg('');
    const cf = parseFloat(stockModal.conversion_factor) || 1;
    const baseQty = (stockUnitMode === 'purchase' && stockModal.purchase_unit_id) ? qty * cf : qty;
    const totalValue = parseFloat(stockVal) || 0;
    const { error } = await inventoryService.setOpeningStock(restaurant.id, stockModal.id, baseQty, totalValue);
    setStockSaving(false);
    if (error) { setStockMsg('Error: ' + (error.message || 'Failed')); return; }
    setItems(prev => prev.map(i => i.id === stockModal.id
      ? { ...i, current_stock: baseQty, opening_stock: baseQty, opening_stock_value: totalValue, avg_unit_cost: baseQty > 0 ? totalValue / baseQty : 0 }
      : i));
    setStockModal(null);
  };

  const filteredItems = items.filter(item => {
    const q = search.toLowerCase();
    if (q && !item.name.toLowerCase().includes(q) && !(item.item_code || '').toLowerCase().includes(q)) return false;
    if (catFilter !== 'all' && item.category_id !== catFilter) return false;
    if (statusFilter === 'active'       && (!item.is_active || item.current_stock <= 0)) return false;
    if (statusFilter === 'low_stock'    && (item.current_stock <= 0 || item.current_stock > (item.min_stock_threshold || 0))) return false;
    if (statusFilter === 'out_of_stock' && item.current_stock > 0) return false;
    return true;
  });

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: T.muted }}>Loading inventory…</div>;
  if (error)   return <div style={{ padding: '20px', color: T.red }}>{error}</div>;

  const cardSt = (color, bg, border) => ({
    background: bg, border: `1px solid ${border}`, borderRadius: '12px',
    padding: '18px 20px', flex: 1, minWidth: '140px',
  });

  const statusPillColor = (key) => ({
    all:          { active: { background: T.text, color: '#fff' },          inactive: { background: '#f3f4f6', color: T.muted } },
    active:       { active: { background: T.green, color: '#fff' },         inactive: { background: '#f0fdf4', color: T.green } },
    low_stock:    { active: { background: T.orange, color: '#fff' },        inactive: { background: '#fff7ed', color: T.orange } },
    out_of_stock: { active: { background: T.red, color: '#fff' },           inactive: { background: T.redBg, color: T.red } },
  }[key]);

  const fld = (label, key, type = 'text', opts) => (
    <div key={key}>
      <label style={{ fontSize: '11px', fontWeight: '700', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '4px' }}>{label}</label>
      {type === 'select' ? (
        <select value={modalForm[key]} onChange={e => setModalForm(f => ({ ...f, [key]: e.target.value }))}
          style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: `1px solid ${T.border}`, fontSize: '13px', outline: 'none', background: '#fff', boxSizing: 'border-box' }}>
          <option value="">— None —</option>
          {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input type={type} value={modalForm[key]} onChange={e => setModalForm(f => ({ ...f, [key]: e.target.value }))}
          style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: `1px solid ${T.border}`, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
      )}
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '800', color: T.text, marginBottom: '4px' }}>Inventory</h2>
        <p style={{ color: T.muted, fontSize: '13px' }}>Stock levels and item management for {restaurant.name}</p>
      </div>

      {/* Subtab bar */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '24px', borderBottom: `1px solid ${T.border}` }}>
        {INV_SUBTABS.map(st => (
          <button key={st.key} onClick={() => setSubTab(st.key)} style={{
            padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: '700', fontSize: '13px',
            color: subTab === st.key ? T.primary : T.muted,
            borderBottom: subTab === st.key ? `2px solid ${T.primary}` : '2px solid transparent',
            marginBottom: '-1px',
          }}>{st.label}</button>
        ))}
      </div>

      {/* ── Dashboard ── */}
      {subTab === 'dashboard' && summary && (
        <div>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '28px' }}>
            <div style={cardSt(T.text, '#f9fafb', T.border)}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Total Items</div>
              <div style={{ fontSize: '28px', fontWeight: '900', color: T.text }}>{summary.totalItems}</div>
              <div style={{ fontSize: '12px', color: T.muted, marginTop: '2px' }}>active items</div>
            </div>
            <div style={cardSt(T.red, T.redBg, T.redBorder)}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: T.red, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Out of Stock</div>
              <div style={{ fontSize: '28px', fontWeight: '900', color: T.red }}>{summary.outOfStockCount}</div>
              <div style={{ fontSize: '12px', color: T.red, marginTop: '2px' }}>need restocking</div>
            </div>
            <div style={cardSt(T.orange, '#fff7ed', '#fed7aa')}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: T.orange, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Low Stock</div>
              <div style={{ fontSize: '28px', fontWeight: '900', color: T.orange }}>{summary.lowStockCount}</div>
              <div style={{ fontSize: '12px', color: T.orange, marginTop: '2px' }}>below threshold</div>
            </div>
            <div style={cardSt(T.green, T.greenBg, T.greenBorder)}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: T.green, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Stock Value</div>
              <div style={{ fontSize: '28px', fontWeight: '900', color: T.green }}>₹{summary.totalValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
              <div style={{ fontSize: '12px', color: T.green, marginTop: '2px' }}>avg cost basis</div>
            </div>
            <div style={cardSt('#7c3aed', '#f5f3ff', '#ddd6fe')}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Weekly Wastage</div>
              <div style={{ fontSize: '28px', fontWeight: '900', color: '#7c3aed' }}>₹{summary.weeklyWastageValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
              <div style={{ fontSize: '12px', color: '#7c3aed', marginTop: '2px' }}>last 7 days</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: '12px', padding: '18px' }}>
              <div style={{ fontSize: '13px', fontWeight: '800', color: T.text, marginBottom: '14px' }}>Needs Attention</div>
              {summary.outOfStockItems.length === 0 && summary.lowStockItems.length === 0 && (
                <p style={{ color: T.muted, fontSize: '13px' }}>All items are adequately stocked.</p>
              )}
              {summary.outOfStockItems.map(i => (
                <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: T.text }}>{i.name}</span>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: T.red, background: T.redBg, border: `1px solid ${T.redBorder}`, borderRadius: '20px', padding: '2px 8px' }}>Out of Stock</span>
                </div>
              ))}
              {summary.lowStockItems.map(i => (
                <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: T.text }}>{i.name}</span>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: T.orange, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '20px', padding: '2px 8px' }}>
                    {Number(i.current_stock).toFixed(2)} / {i.min_stock_threshold} min
                  </span>
                </div>
              ))}
            </div>
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: '12px', padding: '18px' }}>
              <div style={{ fontSize: '13px', fontWeight: '800', color: T.text, marginBottom: '14px' }}>Recent Stock Movements</div>
              {summary.recentMovements.length === 0
                ? <p style={{ color: T.muted, fontSize: '13px' }}>No recent movements.</p>
                : summary.recentMovements.map((m, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: `1px solid ${T.border}` }}>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: T.text }}>{m.inventory_item?.name || '—'}</div>
                      <div style={{ fontSize: '11px', color: T.muted }}>{m.movement_type.replace(/_/g, ' ')}</div>
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: m.quantity_change >= 0 ? T.green : T.red }}>
                      {m.quantity_change >= 0 ? '+' : ''}{Number(m.quantity_change).toFixed(2)}
                    </span>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}

      {/* ── Items ── */}
      {subTab === 'items' && (
        <div>
          {/* Status filter pills */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
            {STATUS_FILTERS.map(sf => {
              const colors = statusPillColor(sf.key);
              const isActive = statusFilter === sf.key;
              return (
                <button key={sf.key} onClick={() => setStatusFilter(sf.key)} style={{
                  padding: '5px 14px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                  fontSize: '12px', fontWeight: '700', transition: 'all 0.15s',
                  ...(isActive ? colors.active : colors.inactive),
                }}>{sf.label}</button>
              );
            })}
          </div>

          {/* Search + category + actions */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text" placeholder="Search by name or code…" value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '8px', border: `1px solid ${T.border}`, fontSize: '13px', outline: 'none', minWidth: '200px' }}
            />
            <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '8px', border: `1px solid ${T.border}`, fontSize: '13px', outline: 'none', background: '#fff', cursor: 'pointer' }}>
              <option value="all">All Categories</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button onClick={load} style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${T.border}`, background: '#fff', fontSize: '13px', cursor: 'pointer', color: T.muted, fontWeight: '600' }}>
              Refresh
            </button>
            <span style={{ fontSize: '13px', color: T.muted }}>{filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}</span>
            <button onClick={openAdd} style={{ marginLeft: 'auto', padding: '8px 18px', borderRadius: '8px', border: 'none', background: T.gradient, color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
              + Add Item
            </button>
          </div>

          {/* Table */}
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: '12px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: `1px solid ${T.border}` }}>
                  {['Name', 'Code', 'Category', 'Type', 'Current Stock', 'Min Threshold', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '700', color: T.muted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: '32px', textAlign: 'center', color: T.muted }}>No items found.</td></tr>
                )}
                {filteredItems.map(item => {
                  const st = stockStatus(item);
                  const isDeleting = deleting === item.id;
                  return (
                    <tr key={item.id} style={{ borderBottom: `1px solid ${T.border}` }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '10px 14px', fontWeight: '600', color: item.is_active ? T.text : T.muted }}>
                        {item.name}
                        {!item.is_active && <span style={{ fontSize: '10px', color: T.muted, marginLeft: '5px' }}>(inactive)</span>}
                      </td>
                      <td style={{ padding: '10px 14px', color: T.muted, fontFamily: 'monospace', fontSize: '12px' }}>{item.item_code || '—'}</td>
                      <td style={{ padding: '10px 14px', color: T.muted }}>{item.category?.name || '—'}</td>
                      <td style={{ padding: '10px 14px', color: T.muted, fontSize: '12px' }}>{ITEM_TYPE_LABELS[item.item_type] || item.item_type || '—'}</td>
                      <td style={{ padding: '10px 14px', fontWeight: '700', color: T.text }}>
                        {Number(item.current_stock || 0).toFixed(2)}
                        {item.base_unit?.short_code && <span style={{ color: T.muted, fontWeight: '400', marginLeft: '3px' }}>{item.base_unit.short_code}</span>}
                        {item.purchase_unit_id && item.purchase_unit_id !== item.base_unit_id && item.purchase_unit && (item.conversion_factor || 1) > 0 && (
                          <span style={{ display: 'block', fontSize: '11px', fontWeight: '400', color: T.muted, marginTop: '1px' }}>
                            = {((item.current_stock || 0) / (item.conversion_factor || 1)).toFixed(2)} {item.purchase_unit.short_code}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px 14px', color: T.muted }}>
                        {item.min_stock_threshold > 0 ? (
                          <>
                            {item.min_stock_threshold}
                            {item.base_unit?.short_code && <span style={{ marginLeft: '3px', fontSize: '11px' }}>{item.base_unit.short_code}</span>}
                            {item.purchase_unit_id && item.purchase_unit_id !== item.base_unit_id && item.purchase_unit && (item.conversion_factor || 1) > 0 && (
                              <span style={{ display: 'block', fontSize: '11px', color: T.muted, marginTop: '1px' }}>
                                = {((item.min_stock_threshold || 0) / (item.conversion_factor || 1)).toFixed(2)} {item.purchase_unit.short_code}
                              </span>
                            )}
                          </>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: '11px', fontWeight: '700', color: st.color, background: st.bg, border: `1px solid ${st.border}`, borderRadius: '20px', padding: '2px 8px', whiteSpace: 'nowrap' }}>
                          {st.label}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => openEdit(item)} style={{ padding: '4px 10px', borderRadius: '6px', border: `1px solid ${T.border}`, background: '#fff', fontSize: '12px', cursor: 'pointer', fontWeight: '600', color: T.muted }}>Edit</button>
                          {(item.current_stock === 0 || item.current_stock === null) && (
                            <button onClick={() => openStockModal(item)} style={{ padding: '4px 10px', borderRadius: '6px', border: `1px solid ${T.green}`, background: '#f0fdf4', fontSize: '12px', cursor: 'pointer', fontWeight: '600', color: T.green, whiteSpace: 'nowrap' }}>Set Opening</button>
                          )}
                          <button onClick={() => handleDelete(item)} disabled={isDeleting} style={{ padding: '4px 10px', borderRadius: '6px', border: `1px solid ${T.redBorder}`, background: T.redBg, fontSize: '12px', cursor: isDeleting ? 'not-allowed' : 'pointer', fontWeight: '600', color: T.red, opacity: isDeleting ? 0.5 : 1 }}>
                            {isDeleting ? '…' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit Item Modal */}
      {modalItem !== null && (
        <Overlay>
          <ModalCard wide>
            <h3 style={{ fontSize: '16px', fontWeight: '800', color: T.text, marginBottom: '16px' }}>
              {modalItem === 'new' ? 'Add Inventory Item' : 'Edit Item'}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {fld('Name *', 'name')}
              {fld('Item Code', 'item_code')}
              {fld('Item Type', 'item_type', 'select', Object.entries(ITEM_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l })))}
              {fld('Category', 'category_id', 'select', categories.map(c => ({ value: c.id, label: c.name })))}
              {fld('Base Unit', 'base_unit_id', 'select', units.map(u => ({ value: u.id, label: `${u.name}${u.short_code ? ` (${u.short_code})` : ''}` })))}
              <div>
                <label style={{ fontSize: '11px', fontWeight: '700', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '4px' }}>Purchase Unit</label>
                <select value={modalForm.purchase_unit_id} onChange={e => setModalForm(f => ({ ...f, purchase_unit_id: e.target.value, conversion_factor: '1' }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: `1px solid ${T.border}`, fontSize: '13px', outline: 'none', background: '#fff', boxSizing: 'border-box' }}>
                  <option value="">Same as base unit</option>
                  {units.map(u => <option key={u.id} value={u.id}>{u.name}{u.short_code ? ` (${u.short_code})` : ''}</option>)}
                </select>
              </div>
              {modalForm.purchase_unit_id && modalForm.purchase_unit_id !== modalForm.base_unit_id ? (
                <div>
                  <label style={{ fontSize: '11px', fontWeight: '700', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '4px' }}>
                    Conversion Factor
                    {modalForm.base_unit_id && modalForm.purchase_unit_id && (
                      <span style={{ fontWeight: '400', textTransform: 'none', marginLeft: '6px' }}>
                        (1 {units.find(u => u.id === modalForm.purchase_unit_id)?.short_code} = ? {units.find(u => u.id === modalForm.base_unit_id)?.short_code})
                      </span>
                    )}
                  </label>
                  <input type="number" min="0" step="any" value={modalForm.conversion_factor}
                    onChange={e => setModalForm(f => ({ ...f, conversion_factor: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: `1px solid ${T.border}`, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ) : <div />}
              {fld('Preferred Supplier', 'preferred_supplier_id', 'select', suppliers.map(s => ({ value: s.id, label: s.name })))}
              <div>
                {(() => {
                  const hasPU = modalForm.purchase_unit_id && modalForm.purchase_unit_id !== modalForm.base_unit_id;
                  const activeUnitId = modalForm.purchase_unit_id || modalForm.base_unit_id;
                  const activeUnit = units.find(u => u.id === activeUnitId);
                  const baseUnit = units.find(u => u.id === modalForm.base_unit_id);
                  const cf = parseFloat(modalForm.conversion_factor) || 1;
                  const thresholdVal = parseFloat(modalForm.min_stock_threshold) || 0;
                  return (
                    <>
                      <label style={{ fontSize: '11px', fontWeight: '700', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '4px' }}>
                        Min Stock Threshold
                        {activeUnit && <span style={{ fontWeight: '400', textTransform: 'none', marginLeft: '6px', color: T.muted }}>— entering in {activeUnit.short_code || activeUnit.name}</span>}
                      </label>
                      <input type="number" min="0" step="any" value={modalForm.min_stock_threshold}
                        onChange={e => setModalForm(f => ({ ...f, min_stock_threshold: e.target.value }))}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: `1px solid ${T.border}`, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
                      {hasPU && thresholdVal > 0 && baseUnit && (
                        <p style={{ fontSize: '11px', color: T.muted, margin: '4px 0 0' }}>
                          = {(thresholdVal * cf).toFixed(2)} {baseUnit.short_code || baseUnit.name} (base unit)
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
            <div style={{ marginTop: '12px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '4px' }}>Notes</label>
              <textarea value={modalForm.notes} onChange={e => setModalForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: `1px solid ${T.border}`, fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
            </div>
            {saveMsg && <p style={{ fontSize: '13px', color: saveMsg.startsWith('Error') ? T.red : T.green, marginTop: '10px' }}>{saveMsg}</p>}
            <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
              <button onClick={() => setModalItem(null)} style={{ flex: 1, padding: '9px', borderRadius: '8px', border: `1px solid ${T.border}`, background: '#fff', cursor: 'pointer', fontWeight: '600', fontSize: '13px', color: T.muted }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ flex: 1, padding: '9px', borderRadius: '8px', border: 'none', background: T.gradient, color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '13px', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving…' : modalItem === 'new' ? 'Add Item' : 'Save Changes'}
              </button>
            </div>
          </ModalCard>
        </Overlay>
      )}

      {/* Opening Stock Modal */}
      {stockModal && (
        <Overlay>
          <ModalCard>
            <h3 style={{ fontSize: '16px', fontWeight: '800', color: T.text, marginBottom: '4px' }}>Set Opening Stock</h3>
            <p style={{ fontSize: '13px', color: T.muted, marginBottom: '16px' }}>{stockModal.name}</p>

            {stockModal.purchase_unit_id && stockModal.purchase_unit_id !== stockModal.base_unit_id && (
              <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                {[
                  { mode: 'purchase', unit: stockModal.purchase_unit, label: 'Purchase Unit' },
                  { mode: 'base',     unit: stockModal.base_unit,     label: 'Base Unit' },
                ].map(({ mode, unit, label }) => (
                  <button key={mode} onClick={() => setStockUnitMode(mode)}
                    style={{ flex: 1, padding: '7px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', border: `1px solid ${stockUnitMode === mode ? T.green : T.border}`, background: stockUnitMode === mode ? '#f0fdf4' : '#fff', color: stockUnitMode === mode ? T.green : T.muted }}>
                    {unit?.short_code || label} ({label})
                  </button>
                ))}
              </div>
            )}

            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '4px' }}>
                Quantity ({stockUnitMode === 'purchase' && stockModal.purchase_unit_id ? stockModal.purchase_unit?.short_code || 'purchase unit' : stockModal.base_unit?.short_code || 'base unit'})
              </label>
              <input type="number" min="0" step="any" value={stockQty} onChange={e => setStockQty(e.target.value)}
                placeholder="0"
                style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: `1px solid ${T.border}`, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              {stockUnitMode === 'purchase' && stockModal.purchase_unit_id && stockModal.purchase_unit_id !== stockModal.base_unit_id && stockQty && !isNaN(parseFloat(stockQty)) && (
                <p style={{ fontSize: '11px', color: T.muted, margin: '4px 0 0' }}>
                  = {(parseFloat(stockQty) * (parseFloat(stockModal.conversion_factor) || 1)).toFixed(2)} {stockModal.base_unit?.short_code || 'base units'} &nbsp;(1 {stockModal.purchase_unit?.short_code} = {stockModal.conversion_factor || 1} {stockModal.base_unit?.short_code})
                </p>
              )}
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '4px' }}>Total Value (₹)</label>
              <input type="number" min="0" step="any" value={stockVal} onChange={e => setStockVal(e.target.value)}
                placeholder="0"
                style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: `1px solid ${T.border}`, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              {stockQty && stockVal && parseFloat(stockQty) > 0 && parseFloat(stockVal) > 0 && (
                <p style={{ fontSize: '11px', color: T.muted, margin: '4px 0 0' }}>
                  Avg cost: ₹{(() => {
                    const cf = parseFloat(stockModal.conversion_factor) || 1;
                    const bq = stockUnitMode === 'purchase' && stockModal.purchase_unit_id ? parseFloat(stockQty) * cf : parseFloat(stockQty);
                    return bq > 0 ? (parseFloat(stockVal) / bq).toFixed(2) : '—';
                  })()} per {stockModal.base_unit?.short_code || 'base unit'}
                </p>
              )}
            </div>

            {stockMsg && <p style={{ fontSize: '13px', color: stockMsg.startsWith('Error') ? T.red : T.green, marginTop: '8px' }}>{stockMsg}</p>}
            <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
              <button onClick={() => setStockModal(null)} style={{ flex: 1, padding: '9px', borderRadius: '8px', border: `1px solid ${T.border}`, background: '#fff', cursor: 'pointer', fontWeight: '600', fontSize: '13px', color: T.muted }}>Cancel</button>
              <button onClick={handleSetOpeningStock} disabled={stockSaving} style={{ flex: 1, padding: '9px', borderRadius: '8px', border: 'none', background: T.gradient, color: '#fff', cursor: stockSaving ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '13px', opacity: stockSaving ? 0.7 : 1 }}>
                {stockSaving ? 'Saving…' : 'Set Opening Stock'}
              </button>
            </div>
          </ModalCard>
        </Overlay>
      )}
    </div>
  );
};

export default AdminRestaurantManagement;
